import { useState, useEffect, useCallback } from "react";

function getStorageKey(hashVitrine) {
  return `vitrine_cart_${hashVitrine || "default"}`;
}

function getCartFromStorage(hashVitrine) {
  try {
    const storageKey = getStorageKey(hashVitrine);
    const data = localStorage.getItem(storageKey);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error("Erro ao ler carrinho do localStorage:", error);
    return [];
  }
}

function setCartToStorage(hashVitrine, cart) {
  try {
    const storageKey = getStorageKey(hashVitrine);
    localStorage.setItem(storageKey, JSON.stringify(cart));
  } catch (error) {
    console.error("Erro ao salvar carrinho no localStorage:", error);
  }
}

export default function useCartManager(hashVitrine) {
  const storageKey = getStorageKey(hashVitrine);
  
  const [cart, setCart] = useState(() => {
    if (typeof window !== "undefined" && hashVitrine) {
      return getCartFromStorage(hashVitrine);
    }
    return [];
  });

  useEffect(() => {
    if (hashVitrine) {
      const savedCart = getCartFromStorage(hashVitrine);
      setCart(savedCart);
    } else {
      setCart([]);
    }
  }, [hashVitrine]);

  useEffect(() => {
    if (hashVitrine && cart.length >= 0) {
      setCartToStorage(hashVitrine, cart);
    }
  }, [cart, hashVitrine]);

  useEffect(() => {
    if (!hashVitrine) return;

    const handleStorageChange = (e) => {
      if (e.key === storageKey && e.newValue) {
        try {
          const newCart = JSON.parse(e.newValue);
          setCart(newCart);
        } catch (error) {
          console.error("Erro ao sincronizar carrinho entre abas:", error);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [hashVitrine, storageKey]);

  const addToCart = useCallback((idProduto, quantidade = 1) => {
    setCart((prev) => {
      const exists = prev.find((item) => item.idProduto === idProduto);
      if (exists) {
        const updated = prev.map((item) =>
          item.idProduto === idProduto
            ? { ...item, quantidade: item.quantidade + quantidade }
            : item
        );
        return updated;
      } else {
        return [...prev, { idProduto, quantidade }];
      }
    });
  }, []);

  const removeFromCart = useCallback((idProduto) => {
    setCart((prev) => {
      const updated = prev.filter((item) => item.idProduto !== idProduto);
      return updated;
    });
  }, []);

  const updateCartQuantity = useCallback((idProduto, quantidade) => {
    if (quantidade <= 0) {
      removeFromCart(idProduto);
      return;
    }
    setCart((prev) =>
      prev.map((item) =>
        item.idProduto === idProduto ? { ...item, quantidade } : item
      )
    );
  }, [removeFromCart]);

  const clearCart = useCallback(() => {
    setCart([]);
    if (hashVitrine) {
      const storageKey = getStorageKey(hashVitrine);
      localStorage.removeItem(storageKey);
    }
  }, [hashVitrine]);

  const getCartItem = useCallback(
    (idProduto) => {
      return cart.find((item) => item.idProduto === idProduto);
    },
    [cart]
  );

  const getTotalItems = useCallback(() => {
    return cart.reduce((sum, item) => sum + item.quantidade, 0);
  }, [cart]);

  const validateCartItems = useCallback((availableProducts) => {
    if (!availableProducts || availableProducts.length === 0) {
      return;
    }
    const validProductIds = new Set(
      availableProducts.map((prod) => prod.idProduto)
    );
    setCart((prev) => {
      const validCart = prev.filter((item) =>
        validProductIds.has(item.idProduto)
      );
      if (validCart.length !== prev.length) {
        return validCart;
      }
      return prev;
    });
  }, []);

  return {
    cart,
    addToCart,
    removeFromCart,
    updateCartQuantity,
    clearCart,
    getCartItem,
    getTotalItems,
    validateCartItems,
  };
}

